import { cwd } from 'node:process';
import React from 'react';
import type { ReactElement } from 'react';
import { AccountAuthProvider } from '@wix/cli-auth';
import type { UserInfo } from '@wix/cli-auth';
import { render } from '@wix/cli-ui-kit';
import { DebugLogProvider } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import { BiProvider } from '@wix/cli-telemetry-react';
import { addUserFields, reportCommandStartEvent } from '@wix/cli-telemetry';
import type { CommandServices } from '@wix/cli-command-contract';
import type { Command } from '@commander-js/extra-typings';
import type { ProjectModel } from '../model.js';
import { loadProjectModel, ProjectModelProvider } from '../model.js';

async function initServices(
  model: ProjectModel,
  { biLogger, errorReporter }: CommandServices
) {
  errorReporter.setContext('astro app info', {
    appId: model.config.appId,
  });

  biLogger.updateDefaults({
    _appId: model.config.appId,
  });
}

export async function renderCommand(
  command: Command,
  { biLogger, errorReporter }: CommandServices,
  cliVersion: string,
  tree: ReactElement
) {
  await runCommand(
    command,
    { biLogger, errorReporter },
    cliVersion,
    async (model) => {
      biLogger.updateDefaults({ command: command.name() });

      const debugLogFilePath = getDebugLogFilePath(model.projectFolder);

      const handleLogin = (userInfo: UserInfo) => {
        errorReporter.setUser({ id: userInfo.userId });
        addUserFields(biLogger, userInfo.userId);
      };

      await render(
        <BiProvider value={biLogger}>
          <ErrorReporterProvider value={errorReporter}>
            <AccountAuthProvider onLogin={handleLogin}>
              <DebugLogProvider filePath={debugLogFilePath}>
                <ProjectModelProvider value={model}>
                  {tree}
                </ProjectModelProvider>
              </DebugLogProvider>
            </AccountAuthProvider>
          </ErrorReporterProvider>
        </BiProvider>
      );
    }
  );
}

export async function runCommand(
  command: Command,
  services: CommandServices,
  cliVersion: string,
  commandFn: (model: ProjectModel) => Promise<void>
) {
  const model = await loadProjectModel(cwd());

  await initServices(model, services);

  services.biLogger.updateDefaults({
    command: command.name(),
  });

  const commandStatus = reportCommandStartEvent({
    biLogger: services.biLogger,
    command,
    cliVersion,
    flow: 'Astro',
    extensions: {},
    extraFields: undefined,
  });

  try {
    await commandFn(model);
    commandStatus.succeed();
  } catch (e) {
    commandStatus.failed(e);
    throw e;
  }
}
